import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TsInputComponent } from 'tsui';

import { FormatNumberPipe } from '@shared/pipes/format-number.pipe';
import { FormFieldComponent } from '../form-field.types';

@Component({
  templateUrl: './percent-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class PercentFormFieldComponent extends FormFieldComponent implements OnInit, DoCheck {
  @ViewChild(TsInputComponent) private readonly tsInput?: TsInputComponent;

  private valueMemento: string;
  private precision: number = 0;

  public editing: boolean = false;
  public valueControl = new UntypedFormControl();
  // Overrides the inherited Material-specific selector: ts-input has no .mat-mdc-text-field-wrapper to target.
  public readonly commonTargetStylingClass = '.ts-input';

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef<HTMLInputElement> | undefined {
    return this.tsInput?.inputElement();
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.fieldDef.meta && this.fieldDef.meta.scale) {
      this.precision = this.fieldDef.meta.scale;
    }

    this.updateDisabledState();

    this.setValues();
  }

  ngDoCheck(): void {
    this.updateDisabledState();
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.setValues();
  }

  edit(): void {
    if (!this.editing && !this.isReadonly) {
      this.editing = true;
      setTimeout(() => {
        this.inputControl?.nativeElement.focus();
      }, 300);
    }
  }

  onBlur(): void {
    this.editing = false;

    // The displayed value carries thousands separators, which Number() can't parse.
    const rawValue = String(this.valueControl.value ?? '').replace(/,/g, '');

    // Percent fields don't evaluate formulas (=...), so any non-numeric input is rejected.
    if (rawValue && !Number.isFinite(Number(rawValue))) {
      this.valueControl.setValue(this.formatValue(this.model[this.fieldDef.name]), { emitEvent: false });
      return;
    }

    const value = rawValue ? String(Number(rawValue)) : null;

    this.valueControl.setValue(this.formatValue(value), { emitEvent: false });

    if (this.valueMemento === value) {
      return;
    }

    this.model[this.fieldDef.name] = value;
    this.update.emit({ [this.fieldDef.name]: value });
  }

  private setValues(): void {
    const value = this.formatValue(this.model[this.fieldDef.name]);
    this.valueControl.setValue(value);
    this.valueMemento = value;
  }

  private formatValue(value: string | null): string | null {
    return new FormatNumberPipe().transform(value, this.precision, this.fieldDef);
  }

  private updateDisabledState(): void {
    if (this.isDisabled === this.valueControl.disabled) {
      return;
    }

    if (this.isDisabled) {
      this.valueControl.disable({ emitEvent: false });
    } else {
      this.valueControl.enable({ emitEvent: false });
    }
  }
}
